import { useState } from 'react';
import type { BulkQuotaUpdateDto } from '../../../types/manageBooks';
import { useTranslation } from '../../../i18n/useTranslation';

/** Fields we emit (all optional). `bookIds` is added by the caller. */
export type QuotaDialogResult = Omit<BulkQuotaUpdateDto, 'bookIds'>;

interface Props {
  open: boolean;
  count: number;
  onCancel: () => void;
  onConfirm: (input: QuotaDialogResult) => void;
  busy: boolean;
}

function parseOptional(v: string): number | undefined {
  if (v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function BookQuotaEditDialog({ open, count, onCancel, onConfirm, busy }: Props) {
  const { t } = useTranslation();
  const [drmToggle, setDrmToggle] = useState<'' | 'on' | 'off'>('');
  const [printLimit, setPrintLimit] = useState('');
  const [printDays,  setPrintDays]  = useState('');
  const [copyLimit,  setCopyLimit]  = useState('');
  const [copyDays,   setCopyDays]   = useState('');
  const [dlLimit,    setDlLimit]    = useState('');
  const [dlDays,     setDlDays]     = useState('');

  if (!open) return null;

  const handleApply = () => {
    onConfirm({
      isDrmEnabled: drmToggle === '' ? undefined : drmToggle === 'on',
      defaultPrintQuotaLimit:        parseOptional(printLimit),
      defaultPrintQuotaWindowDays:   parseOptional(printDays),
      defaultCopyQuotaLimit:         parseOptional(copyLimit),
      defaultCopyQuotaWindowDays:    parseOptional(copyDays),
      defaultDownloadQuotaLimit:     parseOptional(dlLimit),
      defaultDownloadQuotaWindowDays:parseOptional(dlDays),
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      role="dialog" aria-modal="true" aria-labelledby="quota-dialog-title"
    >
      <div className="bg-white rounded shadow-lg p-5 max-w-xl w-full">
        <h2 id="quota-dialog-title" className="text-lg font-semibold mb-1">
          {t('App.Admin.Books.BookQuotaEditDialog.Title', { count })}
        </h2>
        <p className="text-xs text-neutral-500 mb-4">
          {t('App.Admin.Books.BookQuotaEditDialog.Description')}
        </p>

        <label className="flex flex-col text-sm mb-4">
          <span className="text-neutral-700">{t('App.Admin.Books.BookQuotaEditDialog.DrmLabel')}</span>
          <select
            value={drmToggle}
            onChange={e => setDrmToggle(e.target.value as '' | 'on' | 'off')}
            className="border rounded px-3 py-2 min-h-[44px]"
          >
            <option value="">{t('App.Admin.Books.BookQuotaEditDialog.DrmNoChange')}</option>
            <option value="on">{t('App.Admin.Books.BookQuotaEditDialog.DrmEnable')}</option>
            <option value="off">{t('App.Admin.Books.BookQuotaEditDialog.DrmDisable')}</option>
          </select>
        </label>

        <QuotaRow
          limitLabel={t('App.Admin.Books.BookQuotaEditDialog.QuotaLimitLabel', { label: 'Print' })}
          windowLabel={t('App.Admin.Books.BookQuotaEditDialog.QuotaWindowLabel', { label: 'Print' })}
          placeholder={t('App.Admin.Books.BookQuotaEditDialog.QuotaPlaceholder')}
          limit={printLimit} days={printDays} onLimit={setPrintLimit} onDays={setPrintDays}
        />
        <QuotaRow
          limitLabel={t('App.Admin.Books.BookQuotaEditDialog.QuotaLimitLabel', { label: 'Copy' })}
          windowLabel={t('App.Admin.Books.BookQuotaEditDialog.QuotaWindowLabel', { label: 'Copy' })}
          placeholder={t('App.Admin.Books.BookQuotaEditDialog.QuotaPlaceholder')}
          limit={copyLimit} days={copyDays} onLimit={setCopyLimit} onDays={setCopyDays}
        />
        <QuotaRow
          limitLabel={t('App.Admin.Books.BookQuotaEditDialog.QuotaLimitLabel', { label: 'Download' })}
          windowLabel={t('App.Admin.Books.BookQuotaEditDialog.QuotaWindowLabel', { label: 'Download' })}
          placeholder={t('App.Admin.Books.BookQuotaEditDialog.QuotaPlaceholder')}
          limit={dlLimit} days={dlDays} onLimit={setDlLimit} onDays={setDlDays}
        />

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 border rounded min-h-[44px]">{t('App.Admin.Books.BookQuotaEditDialog.Cancel')}</button>
          <button
            type="button" onClick={handleApply} disabled={busy}
            className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50 min-h-[44px]"
          >
            {busy ? t('App.Admin.Books.BookQuotaEditDialog.Applying') : t('App.Admin.Books.BookQuotaEditDialog.Apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface QuotaRowProps {
  limitLabel: string;
  windowLabel: string;
  placeholder: string;
  limit: string;
  days: string;
  onLimit: (v: string) => void;
  onDays: (v: string) => void;
}

function QuotaRow({ limitLabel, windowLabel, placeholder, limit, days, onLimit, onDays }: QuotaRowProps) {
  return (
    <div className="grid grid-cols-2 gap-3 mb-2">
      <label className="flex flex-col text-sm">
        <span className="text-neutral-700">{limitLabel}</span>
        <input
          type="number" min={0}
          value={limit}
          onChange={e => onLimit(e.target.value)}
          placeholder={placeholder}
          className="border rounded px-3 py-2 min-h-[44px]"
        />
      </label>
      <label className="flex flex-col text-sm">
        <span className="text-neutral-700">{windowLabel}</span>
        <input
          type="number" min={0}
          value={days}
          onChange={e => onDays(e.target.value)}
          placeholder={placeholder}
          className="border rounded px-3 py-2 min-h-[44px]"
        />
      </label>
    </div>
  );
}
